import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { invitationService } from '../services/invitation';
import { authService } from '../services/auth';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { ErrorMessage } from '../components/shared/ErrorMessage';
import { CheckCircle, XCircle, Mail, Lock, User, Building } from 'lucide-react';

interface InvitationDetails {
  id: string;
  email: string;
  role: string;
  organization: {
    id: number;
    name: string;
  };
  inviter: {
    name: string;
  };
  expires_at: string;
  status: string;
  user_exists?: boolean;
  requires_registration?: boolean;
}

export const AcceptInvitation: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [stage, setStage] = useState<'loading' | 'expired' | 'register' | 'accept' | 'complete'>('loading');
  
  // Registration form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [registering, setRegistering] = useState(false);

  useEffect(() => {
    if (token) {
      loadInvitation();
    }
  }, [token]);

  const loadInvitation = async () => {
    setLoading(true);
    setError(null);

    try {
      const details = await invitationService.getInvitationDetails(token!);
      setInvitation(details);
      
      if (details.status !== 'pending') {
        setStage('expired');
      } else if (new Date(details.expires_at) < new Date()) {
        setStage('expired');
      } else {
        // Check if user needs to register or just accept
        // The backend now returns user_exists flag
        console.log('Invitation details:', details);
        console.log('User exists:', details.user_exists);
        console.log('Debug info:', details.debug_info);
        setStage(details.user_exists ? 'accept' : 'register');
      }
    } catch (err: any) {
      setError(err.message || 'Invalid or expired invitation');
      setStage('expired');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterAndAccept = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 7) {
      setError('Password must be at least 7 characters long');
      return;
    }

    setRegistering(true);
    setError(null);

    try {
      // Register with invitation token
      await authService.register({
        email: invitation!.email,
        username,
        password,
        firstName,
        lastName,
        invitation_token: token!,
      });

      setStage('complete');
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login', { 
          state: { 
            message: 'Registration successful! Please log in to continue.',
            email: invitation!.email 
          } 
        });
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to complete registration');
    } finally {
      setRegistering(false);
    }
  };

  const handleAcceptOnly = async () => {
    setLoading(true);
    setError(null);

    try {
      await invitationService.acceptInvitation(token!);
      setStage('complete');
      
      // Redirect to login
      setTimeout(() => {
        navigate('/login', { 
          state: { 
            message: 'Invitation accepted! Please log in to access your new organization.',
            email: invitation!.email 
          } 
        });
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to accept invitation');
    } finally {
      setLoading(false);
    }
  };

  if (loading && stage === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="/logo/umbrella-vision-text.png"
            alt="Umbrella Vision"
            className="h-12 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-gray-900">Accept Invitation</h1>
        </div>

        {error && <ErrorMessage message={error} />}

        {/* Expired or Invalid */}
        {stage === 'expired' && (
          <Card>
            <div className="p-6 text-center">
              <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Invitation Invalid</h2>
              <p className="text-gray-600 mb-6">
                This invitation has expired or has already been used.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Please contact your organization administrator to receive a new invitation.
              </p>
              <Link to="/login">
                <Button variant="outline">Back to Login</Button>
              </Link>
            </div>
          </Card>
        )}

        {/* Registration Form */}
        {stage === 'register' && invitation && (
          <Card>
            <div className="p-6">
              <div className="mb-6">
                <h2 className="text-xl font-semibold mb-2">Welcome to Umbrella Vision!</h2>
                <p className="text-gray-600 text-sm">
                  You've been invited by <strong>{invitation.inviter.name}</strong> to join{' '}
                  <strong>{invitation.organization.name}</strong> as {invitation.role === 'analyst' ? 'an' : 'a'}{' '}
                  <strong>{invitation.role}</strong>.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-3 text-sm">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-700">{invitation.email}</span>
                </div>
                <div className="flex items-center gap-3 text-sm mt-2">
                  <Building className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-700">{invitation.organization.name}</span>
                </div>
              </div>

              <form onSubmit={handleRegisterAndAccept} className="space-y-4">
                <Input
                  label="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  placeholder="Choose a username"
                  icon={<User className="w-4 h-4" />}
                />
                
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    placeholder="First name"
                  />
                  <Input
                    label="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    placeholder="Last name"
                  />
                </div>

                <Input
                  type="password"
                  label="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Minimum 7 characters"
                  icon={<Lock className="w-4 h-4" />}
                />

                <Input
                  type="password"
                  label="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="Confirm your password"
                  icon={<Lock className="w-4 h-4" />}
                />

                <Button type="submit" className="w-full" disabled={registering}>
                  {registering ? <LoadingSpinner size="sm" /> : 'Create Account & Accept'}
                </Button>
              </form>
            </div>
          </Card>
        )}

        {/* Accept Only (Existing User) */}
        {stage === 'accept' && invitation && (
          <Card>
            <div className="p-6">
              <div className="mb-6">
                <h2 className="text-xl font-semibold mb-2">New Organization Invitation</h2>
                <p className="text-gray-600 text-sm">
                  You've been invited by <strong>{invitation.inviter.name}</strong> to join{' '}
                  <strong>{invitation.organization.name}</strong> as {invitation.role === 'analyst' ? 'an' : 'a'}{' '}
                  <strong>{invitation.role}</strong>.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-3 text-sm">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-700">{invitation.email}</span>
                </div>
                <div className="flex items-center gap-3 text-sm mt-2">
                  <Building className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-700">{invitation.organization.name}</span>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-6">
                You already have an account with this email. Click below to accept the invitation
                and add this organization to your account.
              </p>

              <Button onClick={handleAcceptOnly} className="w-full" disabled={loading}>
                {loading ? <LoadingSpinner size="sm" /> : 'Accept Invitation'}
              </Button>
            </div>
          </Card>
        )}

        {/* Success */}
        {stage === 'complete' && (
          <Card>
            <div className="p-6 text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Success!</h2>
              <p className="text-gray-600 mb-6">
                Your invitation has been accepted. Redirecting to login...
              </p>
              <LoadingSpinner size="sm" />
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};